import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth, useTrip, PlacesAutocomplete, getPlacesService } from '@trip-planner-monorepo/shared';
import { placesService } from '@trip-planner-monorepo/places';
import { FaPlus, FaTrash, FaMapMarkerAlt } from 'react-icons/fa';
import './Places.css';

function Places() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingPlaceData, setPendingPlaceData] = useState(null);
  const [searchDisplayValue, setSearchDisplayValue] = useState('');
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  // New state for manual entry
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const { currentUser } = useAuth();
  const { tripId } = useParams();
  const { effectiveUserId } = useTrip();

  const fetchPlaces = useCallback(async () => {
    if (!currentUser || !tripId || !effectiveUserId) return;

    setLoading(true);
    try {
      const fetchedPlaces = await placesService.fetchPlaces(effectiveUserId, tripId);
      setPlaces(fetchedPlaces);
    } catch (err) {
      setError('Failed to load places. Please try again.');
      console.error('Error fetching places:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, tripId, effectiveUserId]);

  useEffect(() => {
    fetchPlaces();
  }, [fetchPlaces]);

  // Helper to format place data consistently
  const formatPlaceData = useCallback((placeDetails) => {
    const contextParts = [];
    if (placeDetails.addressComponents.locality) {
      contextParts.push(placeDetails.addressComponents.locality);
    }
    if (placeDetails.addressComponents.administrativeArea) {
      contextParts.push(placeDetails.addressComponents.administrativeArea);
    }
    // Fallback to country if no locality/admin area
    if (contextParts.length === 0 && placeDetails.addressComponents.country) {
      contextParts.push(placeDetails.addressComponents.country);
    }

    return {
      name: placeDetails.name || placeDetails.formattedAddress?.split(',')[0] || '',
      context: contextParts.join(', '),
      type: 'place',
      latitude: placeDetails.coordinates.lat,
      longitude: placeDetails.coordinates.lng,
      placeId: placeDetails.placeId,
      formattedAddress: placeDetails.formattedAddress
    };
  }, []);

  const handlePlaceSelect = useCallback((placeDetails) => {
    const placeData = formatPlaceData(placeDetails);

    setSearchDisplayValue(placeDetails.formattedAddress || placeDetails.name);
    setPendingPlaceData(placeData);
    setInputValue(placeDetails.formattedAddress || placeDetails.name);
  }, [formatPlaceData]);

  const handleInputChange = useCallback((value) => {
    setInputValue(value);
    // If user types, clear pending place data if it doesn't match input
    if (pendingPlaceData && value !== searchDisplayValue) {
      setPendingPlaceData(null);
    }
  }, [pendingPlaceData, searchDisplayValue]);

  const handleAddPlace = async () => {
    // Scenario 1: Place selected from dropdown
    if (pendingPlaceData) {
      try {
        await placesService.createPlace(effectiveUserId, tripId, pendingPlaceData);

        // Clear everything after successful add
        setPendingPlaceData(null);
        setSearchDisplayValue(''); // Clear the display value
        setInputValue('');
        setAutocompleteKey(prev => prev + 1); // Force PlacesAutocomplete to remount with fresh state
        await fetchPlaces();
      } catch (err) {
        setError('Failed to add place. Please try again.');
        console.error('❌ [Places] Error adding place:', err);
      }
      return;
    }

    // Scenario 2: Text typed but no place selected
    if (inputValue && inputValue.trim()) {
      setLoadingSuggestions(true);
      setError('');
      try {
        const placesServiceInstance = getPlacesService();
        // Search for all types of places
        const predictions = await placesServiceInstance.getPlacePredictions(inputValue, { types: [] });

        if (predictions.length === 0) {
          setError('No places found. Please try a different search term.');
        } else {
          setSuggestions(predictions);
          setShowSuggestions(true);
        }
      } catch (err) {
        console.error('Error searching for places:', err);
        setError('Failed to search for places. Please try again.');
      } finally {
        setLoadingSuggestions(false);
      }
    }
  };

  const handleSuggestionSelect = async (prediction) => {
    setLoadingSuggestions(true);
    try {
      const placesServiceInstance = getPlacesService();
      const placeDetails = await placesServiceInstance.getPlaceDetails(prediction.placeId);

      // Use the shared helper to format data
      const placeData = formatPlaceData(placeDetails);

      // Update state to reflect selection (optional but good for consistency)
      handlePlaceSelect(placeDetails);

      // Close suggestions
      setShowSuggestions(false);
      setSuggestions([]);

      // Auto-add the place
      await placesService.createPlace(effectiveUserId, tripId, placeData);

      // Clear everything after successful add
      setPendingPlaceData(null);
      setSearchDisplayValue('');
      setInputValue('');
      setAutocompleteKey(prev => prev + 1);
      await fetchPlaces();

    } catch (err) {
      console.error('Error getting place details:', err);
      setError('Failed to get place details. Please try again.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleDeletePlace = async (placeId) => {
    if (window.confirm('Are you sure you want to delete this place?')) {
      try {
        await placesService.deletePlace(effectiveUserId, tripId, placeId);
        await fetchPlaces();
      } catch (err) {
        setError('Failed to delete place. Please try again.');
        console.error('Error deleting place:', err);
      }
    }
  };

  // Memoize PlacesAutocomplete to prevent re-renders that destroy Google Autocomplete
  const placesAutocompleteComponent = useMemo(() => (
    <PlacesAutocomplete
      key={autocompleteKey}
      onPlaceSelect={handlePlaceSelect}
      onInputChange={handleInputChange}
      placeholder="Add a new place..."
      placeType="geocode"
      allowCustomInput={false}
      className="places-search-input"
    />
  ), [autocompleteKey, handlePlaceSelect, handleInputChange]); // Re-render when key or callback changes

  return (
    <div className="places-content">
      {error && <div className="error-message">{error}</div>}

      <div className="search-section">
        <div className="search-instructions">
          Search for a place below and select it from the dropdown, then click "Add place".
        </div>
        <div className="search-container">
          {placesAutocompleteComponent}
          <button
            onClick={handleAddPlace}
            disabled={(!pendingPlaceData && !inputValue) || loadingSuggestions}
            className="search-button"
            title={pendingPlaceData || inputValue ? 'Click to add place' : 'Type a place name or select from dropdown'}
          >
            <FaPlus /> {loadingSuggestions ? 'Searching...' : 'Add place'}
          </button>
        </div>

        {/* Suggestions Modal/List */}
        {showSuggestions && (
          <div className="suggestions-modal-overlay" onClick={() => setShowSuggestions(false)}>
            <div className="suggestions-modal" onClick={e => e.stopPropagation()}>
              <h3>Select a Place</h3>
              <div className="suggestions-list">
                {suggestions.map(prediction => (
                  <div
                    key={prediction.placeId}
                    className="suggestion-item"
                    onClick={() => handleSuggestionSelect(prediction)}
                  >
                    <div className="suggestion-main-text">
                      {prediction.description.split(',')[0]}
                    </div>
                    <div className="suggestion-secondary-text">
                      {prediction.description}
                    </div>
                  </div>
                ))}
              </div>
              <button className="close-suggestions" onClick={() => setShowSuggestions(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading places...</div>
      ) : (
        <div className="places-list">
          {places.length === 0 ? (
            <div className="no-places">
              <FaMapMarkerAlt className="no-places-icon" />
              <p>No places added yet! Add your first place above.</p>
            </div>
          ) : (
            <ul>
              {places.map(place => (
                <li key={place.id} className="place-item">
                  <div className="place-info">
                    <Link
                      to={`/trips/${tripId}/places/${place.id}`}
                      className="place-name"
                    >
                      {place.name}
                    </Link>
                    {place.context && (
                      <>
                        <span className="separator">:</span>
                        <span className="place-context">{place.context}</span>
                      </>
                    )}
                  </div>
                  <div className="place-actions">
                    <button
                      onClick={() => handleDeletePlace(place.id)}
                      className="delete-button"
                      title="Delete place"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default Places;